import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { onAutocompleteChangeInForm } from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { FeatureFlag } from 'constants/featureFlags';
import {
  PropertyRightsEstateHeld,
  PropertyType,
  TransactionDetailType,
} from 'constants/loanApplication';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { sortCountiesByStates } from 'utils/counties';

import {
  getLocationsCountiesAction,
  getNonUSLocationsAction,
} from 'store/loanApplications/actions';
import {
  selectAlphabeticallySortedCountiesByStates,
  selectIsCountiesExist,
  selectIsFHALoanType,
  selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary,
  selectIsNonUSLocationsExist,
  selectIsPropertyEnrichmentLoading,
  selectIsPurchaseLoanPurposeFromApplication,
  selectIsRefinanceLoanPurposeFromApplication,
  selectIsTbdPropertyAndLoanEditData,
  selectPropertyEnrichmentData,
} from 'store/loanApplications/selectors';

import useMirroredFormValue from '../../../hooks/useMirroredFormValue';
import { appraisalConfigFields } from '../Appraisal/Appraisal.config';
import { defaultForm, defaultInitialFormValues, pagesPropTypes } from '../MainContent.constants';
import { boxStyle } from '../MainContent.styles';

import EnrichmentFieldIndicators from './components/EnrichmentFieldIndicators';
import { getEnrichmentValuesMap } from './enrichmentFieldMapping';
import { getPropertyConfig, propertyConfigFields } from './Property.config';
import { checkIsManufacturedHome, checkIsMixedUseVisible } from './Property.utils';

const Property = ({
  changedFields,
  isLoading,
  isSaving,
  form = defaultForm,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  setMultipleInForm,
  className,
  setIsLoading,
  applicationData,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
  setForm,
  initialFormValues = defaultInitialFormValues,
  setConfig,
}) => {
  const dispatch = useDispatch();
  const formContainerRef = useRef(null);

  const isMilitaryAddressAllowed = useFeatureToggle(FeatureFlag.MilitaryAddressToStates);

  const counties = useSelector(selectAlphabeticallySortedCountiesByStates);
  const isCountiesExist = useSelector(selectIsCountiesExist);
  const isNonUSLocationsExist = useSelector(selectIsNonUSLocationsExist);
  const isPurchase = useSelector(selectIsPurchaseLoanPurposeFromApplication);
  const isRefinance = useSelector(selectIsRefinanceLoanPurposeFromApplication);
  const isAdditionalLoan = useSelector(selectIsLoanPurposeAdditionalLoanFromPropertyAndLoanSummary);
  const isHelocPurposePurchase = useSelector(
    selectIsHelocPurposePurchaseFromPropertyAndLoanSummary,
  );
  const isApplicationTbd = useSelector(selectIsTbdPropertyAndLoanEditData);
  const isFha = useSelector(selectIsFHALoanType);
  const enrichmentData = useSelector(selectPropertyEnrichmentData);
  const isEnrichmentLoading = useSelector(selectIsPropertyEnrichmentLoading);

  const isFormDataEmpty = _.isEmpty(form);
  const entityData = isFormDataEmpty ? applicationData : form;
  const isSubjectProperty = applicationData?.isSubjectProperty;

  const transactionDetailType =
    form?.transactionDetail?.value || applicationData?.transactionDetailType;
  const isConstructionToPermanent =
    transactionDetailType === TransactionDetailType.CONSTRUCTION_CONVERSATION_OR_TO_PERMANENT;

  const enrichmentValuesMap = useMemo(
    () => getEnrichmentValuesMap(enrichmentData),
    [enrichmentData],
  );

  const onChangeAddressState = useCallback(
    (e, option) => {
      onAutocompleteChangeInForm({ validateField, setInForm })(e, option);

      setInForm('county', '', false);
      validateField('county', '');
    },
    [setInForm, validateField],
  );

  useMirroredFormValue({
    form,
    setInForm,
    path: propertyConfigFields.locationType.nameInModel,
    mirrorPath: appraisalConfigFields.locationType.nameInModel,
  });

  useEffectSafeStrictMode(() => {
    if (isNonUSLocationsExist) {
      return;
    }

    dispatch(getNonUSLocationsAction()).catch(Error);
  }, [dispatch, isNonUSLocationsExist]);

  useEffectSafeStrictMode(() => {
    if (isCountiesExist) {
      return;
    }

    setIsLoading(true);

    dispatch(getLocationsCountiesAction())
      .then(({ payload: rawCounties }) => {
        setConfig(prev => {
          const targetGroupIndex = prev.groups.findIndex(({ key }) => key === 'property-address');

          if (targetGroupIndex === -1) {
            return prev;
          }

          const countyFieldIndex = prev.groups[targetGroupIndex].fields.findIndex(
            ({ nameInModel }) => nameInModel === 'county',
          );

          const countyField = prev.groups[targetGroupIndex].fields[countyFieldIndex];

          if (countyField.inputProps.options.length) {
            return prev;
          }

          const addressState = applicationData.state;
          const counties = sortCountiesByStates(rawCounties);
          const addressCounties = addressState ? counties[addressState] : counties.ALL_COUNTIES;

          const updatedConfig = _.cloneDeep(prev);

          updatedConfig.groups[targetGroupIndex].fields[countyFieldIndex].inputProps.options =
            addressCounties;

          return updatedConfig;
        });
      })
      .catch(_.noop)
      .finally(() => setIsLoading(false));
  }, [dispatch, isCountiesExist, setIsLoading, applicationData, setConfig]);

  useEffect(() => {
    if (isFormDataEmpty || form.isDeedRestricted || !form.deedRestrictionType) {
      return;
    }

    setMultipleInForm(
      {
        deedRestrictionType: null,
        deedRestrictionMonths: null,
      },
      false,
    );
  }, [form.deedRestrictionType, form.isDeedRestricted, isFormDataEmpty, setMultipleInForm]);

  useEffect(() => {
    if (isFormDataEmpty || form.hasAffordableLegalRestrictions) {
      return;
    }

    setInForm('isConveyable', null, false);
  }, [form.hasAffordableLegalRestrictions, isFormDataEmpty, setInForm]);

  useEffect(() => {
    const isLeasehold =
      form.propertyRightsEstateHeld?.value === PropertyRightsEstateHeld.LEASE_HOLD;

    if (isFormDataEmpty || isLeasehold || !form.leaseholdExpiration) {
      return;
    }

    setInForm('leaseholdExpiration', null, false);
  }, [form.propertyRightsEstateHeld?.value, form.leaseholdExpiration, isFormDataEmpty, setInForm]);

  useEffect(() => {
    if (isFormDataEmpty) {
      return;
    }

    setForm(prev => {
      const formattedForm = { ...prev };

      const propertyTypeValue = prev.propertyType?.value;
      const numberOfUnitsValue = prev.numberOfUnits?.value;
      const isMultiUnit = propertyTypeValue === PropertyType.MULTI_UNIT;

      const pathToUnits = 'appraisal.uniformCollateralDataPortal.units';

      const currentUnits = _.get(prev, pathToUnits);
      const initialUnits = _.get(initialFormValues, pathToUnits);

      if (!isMultiUnit) {
        _.unset(formattedForm, 'numberOfUnits');
      }

      if (currentUnits && initialUnits) {
        const unitsValue = (isMultiUnit && numberOfUnitsValue) || 1;

        const trimmedUnits = currentUnits.slice(0, unitsValue);

        if (unitsValue > trimmedUnits.length) {
          const missedUtils = initialUnits.slice(trimmedUnits.length, unitsValue);

          trimmedUnits.push(...missedUtils);
        }

        _.set(formattedForm, pathToUnits, trimmedUnits);
      }

      return formattedForm;
    });
  }, [form.numberOfUnits, form.propertyType, isFormDataEmpty, setForm, initialFormValues]);

  const config = useMemo(() => {
    const addressState = entityData?.state?.value;
    const addressCounties = addressState ? counties[addressState] : counties.ALL_COUNTIES;
    const isDeedRestricted = entityData?.isDeedRestricted;
    const isMultiUnit = entityData?.propertyType?.value === PropertyType.MULTI_UNIT;
    const isCondo = entityData?.propertyType?.value === PropertyType.CONDO;
    const isManufacturedHome = checkIsManufacturedHome(entityData?.propertyType?.value);
    const isLeasehold =
      entityData?.propertyRightsEstateHeld?.value === PropertyRightsEstateHeld.LEASE_HOLD;
    const isMixedUseVisible = checkIsMixedUseVisible({
      occupancy: entityData?.occupancy?.value,
      propertyType: entityData?.propertyType?.value,
    });
    const isCompletionDateVisible = entityData?.isNewConstruction;
    const hasAffordableLegalRestrictions = entityData?.hasAffordableLegalRestrictions;

    return getPropertyConfig({
      addressCounties,
      isMilitaryAddressAllowed,
      isPurchase,
      isDeedRestricted,
      isConstructionToPermanent,
      isRefinance,
      isAdditionalLoan,
      isHelocPurposePurchase,
      isMultiUnit,
      isMixedUseVisible,
      isLeasehold,
      isApplicationTbd,
      isSubjectProperty,
      onChangeAddressState,
      state: addressState,
      isManufacturedHome,
      isCompletionDateVisible,
      isFha,
      hasAffordableLegalRestrictions,
      isCondo,
    });
  }, [
    entityData?.state?.value,
    entityData?.isDeedRestricted,
    entityData?.propertyType?.value,
    entityData?.propertyRightsEstateHeld?.value,
    entityData?.occupancy?.value,
    entityData?.isNewConstruction,
    entityData?.hasAffordableLegalRestrictions,
    counties,
    isRefinance,
    isAdditionalLoan,
    isHelocPurposePurchase,
    isMilitaryAddressAllowed,
    isPurchase,
    isConstructionToPermanent,
    isApplicationTbd,
    isSubjectProperty,
    onChangeAddressState,
    isFha,
  ]);

  useEffect(() => {
    if (!config) {
      return;
    }

    setEntityConfig(config);
  }, [config, setEntityConfig]);

  if (!config) {
    return null;
  }

  return (
    <Box className={className}>
      <Box ref={formContainerRef} sx={boxStyle}>
        <EnrichmentFieldIndicators
          containerRef={formContainerRef}
          enrichmentValuesMap={enrichmentValuesMap}
          form={form}
          isLoading={isEnrichmentLoading}
          setInForm={setInForm}
          validateField={validateField}
        />
        <FormGenerator
          changedFields={changedFields}
          config={config}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.PROPERTY_ADDRESS}
          handleChange={handleChange}
          isDisabled={!isCountiesExist}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </Box>
    </Box>
  );
};

Property.propTypes = pagesPropTypes;

export default memo(Property);
